(() => {
  const completedClass = 'caffeine-heading-animate__char-item--complete';
  const completedHeadingClass = 'caffeine-heading-animate--complete';
  const animationNames = new Set(['caffeine-heading-blur', 'caffeine-heading-split']);
  const cleanupTimers = new WeakMap();
  const animationStartedHeadings = new WeakSet();
  const observedHeadings = new WeakSet();

  const timeToMilliseconds = (value) => {
    const time = Number.parseFloat(value);
    if (!Number.isFinite(time)) return 0;

    return value.trim().endsWith('ms') ? time : time * 1000;
  };

  const getLongestAnimationTime = (heading) => {
    return Array.from(heading.querySelectorAll('.caffeine-heading-animate__char-item')).reduce(
      (longestTime, item) => {
        const styles = window.getComputedStyle(item);
        const durations = styles.animationDuration.split(',').map(timeToMilliseconds);
        const delays = styles.animationDelay.split(',').map(timeToMilliseconds);
        const itemTime = Math.max(
          0,
          ...durations.map(
            (duration, index) =>
              duration + (typeof delays[index] === 'number' ? delays[index] : delays[0] || 0)
          )
        );

        return Math.max(longestTime, itemTime);
      },
      0
    );
  };

  const forceSafariRepaint = (element) => {
    const originalDisplay = element.style.display;

    element.style.display = 'none';
    void element.offsetHeight;
    element.style.display = originalDisplay;
    void element.offsetHeight;
  };

  const finalizeHeading = (heading) => {
    if (!heading || heading.classList.contains(completedHeadingClass)) return;

    const timer = cleanupTimers.get(heading);
    if (timer) window.clearTimeout(timer);
    cleanupTimers.delete(heading);

    heading.querySelectorAll('.caffeine-heading-animate__word').forEach((word) => {
      const text = Array.from(word.children)
        .filter((child) => child.classList.contains('caffeine-heading-animate__char'))
        .map((character) => {
          if (character.classList.contains('caffeine-heading-animate__char--space')) {
            return '\u00a0';
          }

          const characterItem = character.matches('.caffeine-heading-animate__char-item')
            ? character
            : character.querySelector('.caffeine-heading-animate__char-item');

          return characterItem ? characterItem.textContent : '';
        })
        .join('');

      while (word.firstChild) word.removeChild(word.firstChild);
      word.appendChild(document.createTextNode(text));
    });

    heading.classList.add(completedHeadingClass);
    forceSafariRepaint(heading);

    // A new DOM node cannot reuse the stale filtered texture cached for the old heading.
    const freshHeading = heading.cloneNode(true);
    heading.replaceWith(freshHeading);
    forceSafariRepaint(freshHeading);
  };

  const scheduleHeadingCleanup = (heading, restartTimer) => {
    if (!heading || heading.classList.contains(completedHeadingClass)) return;
    if (cleanupTimers.has(heading) && !restartTimer) return;

    const existingTimer = cleanupTimers.get(heading);
    if (existingTimer) window.clearTimeout(existingTimer);

    const cleanupDelay = Math.max(getLongestAnimationTime(heading) + 150, 500);
    cleanupTimers.set(
      heading,
      window.setTimeout(() => finalizeHeading(heading), cleanupDelay)
    );
  };

  const handleAnimationStart = (event) => {
    const animationName = event.animationName || event.webkitAnimationName;
    if (!animationNames.has(animationName)) return;
    if (!event.target.classList.contains('caffeine-heading-animate__char-item')) return;

    const heading = event.target.closest('.caffeine-heading-animate');
    if (!heading || animationStartedHeadings.has(heading)) return;

    animationStartedHeadings.add(heading);
    scheduleHeadingCleanup(heading, true);
  };

  const markComplete = (event) => {
    const animationName = event.animationName || event.webkitAnimationName;
    if (!animationNames.has(animationName)) return;
    if (!event.target.classList.contains('caffeine-heading-animate__char-item')) return;

    event.target.classList.add(completedClass);

    const heading = event.target.closest('.caffeine-heading-animate');
    if (
      heading &&
      !heading.querySelector(
        `.caffeine-heading-animate__char-item:not(.${completedClass})`
      )
    ) {
      finalizeHeading(heading);
    }
  };

  const intersectionObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;

              intersectionObserver.unobserve(entry.target);
              scheduleHeadingCleanup(entry.target, false);
            });
          },
          {
            rootMargin: '0px 0px -50px 0px',
            threshold: 0.1,
          }
        )
      : null;

  const observeHeading = (heading) => {
    if (
      observedHeadings.has(heading) ||
      heading.classList.contains(completedHeadingClass)
    ) {
      return;
    }

    observedHeadings.add(heading);

    if (intersectionObserver) {
      intersectionObserver.observe(heading);
    } else {
      scheduleHeadingCleanup(heading, false);
    }
  };

  const observeHeadings = (container) => {
    if (container.matches && container.matches('.caffeine-heading-animate')) {
      observeHeading(container);
    }

    if (!container.querySelectorAll) return;
    container.querySelectorAll('.caffeine-heading-animate').forEach(observeHeading);
  };

  document.addEventListener('animationstart', handleAnimationStart);
  document.addEventListener('webkitAnimationStart', handleAnimationStart);
  document.addEventListener('animationend', markComplete);
  document.addEventListener('webkitAnimationEnd', markComplete);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => observeHeadings(document), {
      once: true,
    });
  } else {
    observeHeadings(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    observeHeadings(event.target);
  });
})();
